import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { apiClient } from '../../lib/api-client';
import type { AuditResourceRow, ProposedStep } from './ReconciliationPreviewDialog';
import {
  describeStreamFailure,
  progressRowsAfterStreamError,
} from './role-reconciliation-progress.utils';

interface RoleBotUserInstall {
  userId: string;
  displayName: string;
  email: string;
  installed: boolean;
}

interface OperationProgressRow {
  id: string;
  name: string;
  status: string;
  error?: string;
}

interface RoleReconciliationDialogProps {
  open: boolean;
  streamId: string;
  roleCode: string;
  roleLabel: string;
  onClose: () => void;
  onChanged?: () => void;
}

type DeletionConfirmationTarget =
  | { kind: 'catchup-delete'; items: ProposedStep[] }
  | { kind: 'deprovision'; items: ProposedStep[] }
  | { kind: 'uninstall-users'; items: RoleBotUserInstall[] }
  | { kind: 'uninstall-identifiers'; items: string[] };

function uniqSorted(ids: Iterable<string>): string[] {
  return [...new Set(Array.from(ids).map((x) => String(x).trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
}

function isDeleteStep(step: ProposedStep): boolean {
  return String(step.action || '').toLowerCase() === 'delete';
}

function parseUserIdentifierText(value: string): string[] {
  return uniqSorted(value.split(/[\s,;]+/));
}

export function RoleReconciliationDialog({
  open,
  streamId,
  roleCode,
  roleLabel,
  onClose,
  onChanged,
}: RoleReconciliationDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressTitle, setProgressTitle] = useState('');
  const [progressRows, setProgressRows] = useState<OperationProgressRow[]>([]);

  const [auditRows, setAuditRows] = useState<AuditResourceRow[]>([]);
  const [catchUpSteps, setCatchUpSteps] = useState<ProposedStep[]>([]);
  const [deprovisionSteps, setDeprovisionSteps] = useState<ProposedStep[]>([]);
  const [botUsersSupported, setBotUsersSupported] = useState(false);
  const [botUsersWarning, setBotUsersWarning] = useState<string | null>(null);
  const [botUsers, setBotUsers] = useState<RoleBotUserInstall[]>([]);

  const [selectedCatchUp, setSelectedCatchUp] = useState<Set<string>>(new Set());
  const [selectedDeprovision, setSelectedDeprovision] = useState<Set<string>>(new Set());
  const [selectedInstallUsers, setSelectedInstallUsers] = useState<Set<string>>(new Set());
  const [selectedUninstallUsers, setSelectedUninstallUsers] = useState<Set<string>>(new Set());
  const [explicitBotUserText, setExplicitBotUserText] = useState('');
  const [deletionConfirmation, setDeletionConfirmation] =
    useState<DeletionConfirmationTarget | null>(null);

  const loadAll = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const [auditRes, catchUpRes, deprovisionRes, botUsersRes] = await Promise.all([
        apiClient.auditRoleResources(streamId, roleCode),
        apiClient.previewRoleCatchUpReconciliation(streamId, roleCode),
        apiClient.previewRoleDeprovisionReconciliation(streamId, roleCode),
        apiClient.listRoleBotUsers(streamId, roleCode),
      ]);
      const nextAuditRows = (auditRes.discrepancies || []) as AuditResourceRow[];
      const nextCatchUpSteps = (catchUpRes.proposedSteps || []) as ProposedStep[];
      const nextDeprovisionSteps = (deprovisionRes.proposedSteps || []) as ProposedStep[];
      const nextUsers = (botUsersRes.users || []) as RoleBotUserInstall[];

      setAuditRows(nextAuditRows);
      setCatchUpSteps(nextCatchUpSteps);
      setDeprovisionSteps(nextDeprovisionSteps);
      setBotUsersSupported(Boolean(botUsersRes.supported));
      setBotUsersWarning(botUsersRes.warning || null);
      setBotUsers(nextUsers);
      setSelectedCatchUp(new Set(nextCatchUpSteps.filter((s) => s.defaultSelected).map((s) => s.id)));
      setSelectedDeprovision(
        new Set(nextDeprovisionSteps.filter((s) => s.defaultSelected).map((s) => s.id)),
      );
      setSelectedInstallUsers(new Set());
      setSelectedUninstallUsers(new Set());
    } catch (e: any) {
      setError(e?.message || 'Failed to load role reconciliation data');
    } finally {
      setLoading(false);
    }
  }, [open, streamId, roleCode]);

  useEffect(() => {
    if (!open) return;
    setStatusMsg(null);
    void loadAll();
  }, [open, loadAll]);

  const missingRows = useMemo(
    () => auditRows.filter((r) => String(r.status) === 'missing_external'),
    [auditRows],
  );
  const existingRows = useMemo(
    () => auditRows.filter((r) => String(r.status) === 'in_sync'),
    [auditRows],
  );

  const usersInstalled = useMemo(() => botUsers.filter((u) => u.installed), [botUsers]);
  const usersNotInstalled = useMemo(() => botUsers.filter((u) => !u.installed), [botUsers]);
  const selectedCatchUpSteps = useMemo(
    () => catchUpSteps.filter((step) => selectedCatchUp.has(step.id)),
    [catchUpSteps, selectedCatchUp],
  );
  const selectedDeprovisionSteps = useMemo(
    () => deprovisionSteps.filter((step) => selectedDeprovision.has(step.id)),
    [deprovisionSteps, selectedDeprovision],
  );
  const selectedUninstallBotUsers = useMemo(
    () => botUsers.filter((user) => selectedUninstallUsers.has(user.userId)),
    [botUsers, selectedUninstallUsers],
  );
  const explicitBotUserIdentifiers = useMemo(
    () => parseUserIdentifierText(explicitBotUserText),
    [explicitBotUserText],
  );

  const toggleSelection = (setter: Dispatch<SetStateAction<Set<string>>>, id: string) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const startProgress = (title: string) => {
    setProgressTitle(title);
    setProgressRows([]);
    setProgressOpen(true);
  };

  const markStreamFailed = (error: unknown) => {
    const message = describeStreamFailure(error);
    setError(message);
    setProgressRows((prev) => progressRowsAfterStreamError(prev, message));
    setProgressOpen(true);
  };

  const handleProgressEvent = (event: any) => {
    if (event?.type === 'error') {
      throw new Error(event.error || 'Operation failed');
    }
    if (event?.type !== 'step' && event?.type !== 'user') return;
    const id = event.id ? `${event.type}:${event.id}` : `${event.type}:${event.name}`;
    setProgressRows((prev) => {
      const next = [...prev];
      const index = next.findIndex((row) => row.id === id);
      const row = {
        id,
        name: event.name,
        status: event.status,
        error: event.error,
      };
      if (index >= 0) next[index] = row;
      else next.push(row);
      return next;
    });
  };

  const runCatchUp = async () => {
    const approvedStepIds = uniqSorted(selectedCatchUp);
    setBusy('catchup');
    setError(null);
    try {
      let completedSteps = 0;
      startProgress('Provisioning role resources');
      await apiClient.streamExecuteReconciliation(
        {
          streamId,
          entityType: 'role',
          entityId: roleCode,
          mode: 'catchup',
          approvedStepIds,
        },
        (event) => {
          if (event?.type === 'complete') completedSteps = event.steps?.length || 0;
          handleProgressEvent(event);
        },
      );
      setStatusMsg(`Catch-up finished: ${completedSteps} step result(s).`);
      setBusy(null);
      await loadAll();
      onChanged?.();
    } catch (e: any) {
      markStreamFailed(e);
    } finally {
      setBusy(null);
    }
  };

  const confirmCatchUp = () => {
    const deleteSteps = selectedCatchUpSteps.filter(isDeleteStep);
    if (deleteSteps.length > 0) {
      setDeletionConfirmation({ kind: 'catchup-delete', items: deleteSteps });
      return;
    }
    void runCatchUp();
  };

  const runDeprovision = async () => {
    const approvedStepIds = uniqSorted(selectedDeprovision);
    setBusy('deprovision');
    setError(null);
    try {
      let completedSteps = 0;
      startProgress('Deprovisioning role resources');
      await apiClient.streamExecuteReconciliation(
        {
          streamId,
          entityType: 'role',
          entityId: roleCode,
          mode: 'deprovision',
          approvedStepIds,
        },
        (event) => {
          if (event?.type === 'complete') completedSteps = event.steps?.length || 0;
          handleProgressEvent(event);
        },
      );
      setStatusMsg(`Uninstall finished: ${completedSteps} step result(s).`);
      setBusy(null);
      await loadAll();
      onChanged?.();
    } catch (e: any) {
      markStreamFailed(e);
    } finally {
      setBusy(null);
    }
  };

  const confirmDeprovision = () => {
    if (selectedDeprovisionSteps.length === 0) return;
    setDeletionConfirmation({ kind: 'deprovision', items: selectedDeprovisionSteps });
  };

  const installUsers = async () => {
    const userIds = uniqSorted(selectedInstallUsers);
    await installBotUserIdentifiers(userIds);
  };

  const installBotUserIdentifiers = async (userIds: string[]) => {
    setBusy('install-users');
    setError(null);
    try {
      let installed = 0;
      startProgress('Installing bot for users');
      await apiClient.streamInstallRoleBotForUsers(streamId, roleCode, userIds, (event) => {
        if (event?.type === 'complete') installed = event.installed || 0;
        handleProgressEvent(event);
      });
      setStatusMsg(`Installed bot for ${installed} user(s).`);
      setBusy(null);
      const botUsersRes = await apiClient.listRoleBotUsers(streamId, roleCode);
      setBotUsersSupported(Boolean(botUsersRes.supported));
      setBotUsersWarning(botUsersRes.warning || null);
      setBotUsers((botUsersRes.users || []) as RoleBotUserInstall[]);
      setSelectedInstallUsers(new Set());
      setExplicitBotUserText('');
      onChanged?.();
    } catch (e: any) {
      markStreamFailed(e);
    } finally {
      setBusy(null);
    }
  };

  const uninstallUsers = async () => {
    const userIds = uniqSorted(selectedUninstallUsers);
    await uninstallBotUserIdentifiers(userIds, 'selected users');
  };

  const uninstallBotUserIdentifiers = async (userIds: string[], label = 'user(s)') => {
    setBusy('uninstall-users');
    setError(null);
    try {
      let removed = 0;
      startProgress('Uninstalling bot from users');
      await apiClient.streamUninstallRoleBotForUsers(streamId, roleCode, userIds, (event) => {
        if (event?.type === 'complete') removed = event.removed || 0;
        handleProgressEvent(event);
      });
      setStatusMsg(`Uninstalled bot for ${removed} ${label}.`);
      setBusy(null);
      const botUsersRes = await apiClient.listRoleBotUsers(streamId, roleCode);
      setBotUsersSupported(Boolean(botUsersRes.supported));
      setBotUsersWarning(botUsersRes.warning || null);
      setBotUsers((botUsersRes.users || []) as RoleBotUserInstall[]);
      setSelectedUninstallUsers(new Set());
      setExplicitBotUserText('');
      onChanged?.();
    } catch (e: any) {
      markStreamFailed(e);
    } finally {
      setBusy(null);
    }
  };

  const confirmUninstallUsers = () => {
    if (selectedUninstallBotUsers.length === 0) return;
    setDeletionConfirmation({ kind: 'uninstall-users', items: selectedUninstallBotUsers });
  };

  const installExplicitUsers = () => {
    if (explicitBotUserIdentifiers.length === 0) return;
    void installBotUserIdentifiers(explicitBotUserIdentifiers);
  };

  const confirmUninstallExplicitUsers = () => {
    if (explicitBotUserIdentifiers.length === 0) return;
    setDeletionConfirmation({ kind: 'uninstall-identifiers', items: explicitBotUserIdentifiers });
  };

  const confirmDeletion = () => {
    const target = deletionConfirmation;
    setDeletionConfirmation(null);
    if (!target) return;
    if (target.kind === 'uninstall-identifiers') {
      void uninstallBotUserIdentifiers(target.items, 'explicit user(s)');
      return;
    }
    if (target.kind === 'uninstall-users') {
      void uninstallUsers();
      return;
    }
    if (target.kind === 'catchup-delete') {
      void runCatchUp();
      return;
    }
    void runDeprovision();
  };

  const deletionConfirmationTitle =
    deletionConfirmation?.kind === 'uninstall-users' ||
    deletionConfirmation?.kind === 'uninstall-identifiers'
      ? 'Confirm bot uninstall'
      : 'Confirm resource deletion';
  const deletionConfirmationDescription =
    deletionConfirmation?.kind === 'uninstall-users' ||
    deletionConfirmation?.kind === 'uninstall-identifiers'
      ? 'This will uninstall the Teams bot from the selected users for this role.'
      : deletionConfirmation?.kind === 'catchup-delete'
        ? 'This catch-up run includes delete actions. Confirm the external resources that will be deleted.'
        : 'This will delete or uninstall the selected external resources for this role.';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Keep this dialog fully controlled by explicit actions in this component.
        // Backdrop clicks / escape from the shared Dialog wrapper can race with the
        // role-card dropdown interaction and close immediately after open.
        if (next) return;
      }}
    >
      <DialogContent maxWidth="4xl">
        <DialogHeader>
          <DialogTitle>Role resource reconciliation</DialogTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 pr-8">
            {roleLabel} ({roleCode}) in stream {streamId}
          </p>
        </DialogHeader>
        <DialogBody>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading role reconciliation data...
            </div>
          )}
          {error && (
            <div className="mb-4 flex gap-2 rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
              <AlertTriangle className="w-5 h-5 shrink-0" aria-hidden />
              <span>{error}</span>
            </div>
          )}
          {statusMsg && !error && (
            <div className="mb-4 rounded-md bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-700 dark:text-green-300">
              {statusMsg}
            </div>
          )}
          {progressOpen && (
            <section className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {progressTitle || 'Operation progress'}
              </h3>
              {progressRows.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {busy ? 'Waiting for the first activity...' : 'No activity was reported.'}
                </div>
              ) : (
                <ul className="space-y-2 max-h-52 overflow-y-auto">
                  {progressRows.map((row) => {
                    const variant =
                      row.status === 'completed'
                        ? 'success'
                        : row.status === 'failed'
                          ? 'destructive'
                          : row.status === 'running' || row.status === 'resolving'
                            ? 'default'
                            : 'outline';
                    return (
                      <li key={row.id} className="rounded border border-gray-200 dark:border-gray-700 p-2 text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          {(row.status === 'running' || row.status === 'resolving') && (
                            <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                          )}
                          <Badge variant={variant}>{row.status}</Badge>
                          <span className="font-medium text-gray-900 dark:text-gray-100 break-all">
                            {row.name}
                          </span>
                        </div>
                        {row.error && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400 break-all">
                            {row.error}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}
          {!loading && (
            <div className="space-y-5">
              <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Catch up missing resources
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Missing externally: {missingRows.length}. Proposed create/update actions:{' '}
                    {catchUpSteps.length}.
                  </p>
                </div>
                <ul className="space-y-2 max-h-52 overflow-y-auto">
                  {catchUpSteps.map((step) => (
                    <li key={step.id} className="flex items-start gap-3 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={selectedCatchUp.has(step.id)}
                        onChange={() => toggleSelection(setSelectedCatchUp, step.id)}
                        disabled={Boolean(busy)}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant={step.action === 'delete' ? 'destructive' : 'success'}>
                            {step.action}
                          </Badge>
                          <span className="font-medium text-gray-900 dark:text-gray-100 break-all">
                            {step.resourceName}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{step.reason}</p>
                      </div>
                    </li>
                  ))}
                  {catchUpSteps.length === 0 && (
                    <li className="text-xs text-gray-500 dark:text-gray-400">No catch-up actions.</li>
                  )}
                </ul>
                <Button
                  variant="default"
                  size="sm"
                  disabled={Boolean(busy) || selectedCatchUp.size === 0}
                  onClick={confirmCatchUp}
                >
                  {busy === 'catchup' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Provision selected
                </Button>
              </section>

              <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Existing resources for this role
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Existing externally: {existingRows.length}. Proposed uninstall actions:{' '}
                    {deprovisionSteps.length}.
                  </p>
                </div>
                <ul className="space-y-2 max-h-52 overflow-y-auto">
                  {deprovisionSteps.map((step) => (
                    <li key={step.id} className="flex items-start gap-3 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={selectedDeprovision.has(step.id)}
                        onChange={() => toggleSelection(setSelectedDeprovision, step.id)}
                        disabled={Boolean(busy)}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="destructive">{step.action}</Badge>
                          <span className="font-medium text-gray-900 dark:text-gray-100 break-all">
                            {step.resourceName}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{step.reason}</p>
                      </div>
                    </li>
                  ))}
                  {deprovisionSteps.length === 0 && (
                    <li className="text-xs text-gray-500 dark:text-gray-400">
                      No uninstall actions available.
                    </li>
                  )}
                </ul>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={Boolean(busy) || selectedDeprovision.size === 0}
                  onClick={confirmDeprovision}
                >
                  {busy === 'deprovision' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Uninstall selected
                </Button>
              </section>

              {!botUsersSupported && botUsersWarning && (
                <section className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
                  <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                    Bot user installs unavailable
                  </h3>
                  <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                    {botUsersWarning}
                  </p>
                </section>
              )}

              <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Install or uninstall bot by user
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Paste email addresses or Graph user IDs, separated by commas, spaces, or new lines.
                  </p>
                </div>
                <textarea
                  className="min-h-24 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                  value={explicitBotUserText}
                  onChange={(event) => setExplicitBotUserText(event.target.value)}
                  placeholder="ivan.milic@maxiron.com&#10;another.user@maxiron.com"
                  disabled={Boolean(busy)}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    disabled={Boolean(busy) || explicitBotUserIdentifiers.length === 0}
                    onClick={installExplicitUsers}
                  >
                    {busy === 'install-users' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Install for listed users
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={Boolean(busy) || explicitBotUserIdentifiers.length === 0}
                    onClick={confirmUninstallExplicitUsers}
                  >
                    {busy === 'uninstall-users' ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Uninstall from listed users
                  </Button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Parsed users: {explicitBotUserIdentifiers.length}
                  </span>
                </div>
              </section>

              {botUsersSupported && (
                <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Bot user installs
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Install bot for new people or uninstall from selected users.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="rounded border border-gray-200 dark:border-gray-700 p-2">
                      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Not installed ({usersNotInstalled.length})
                      </div>
                      <ul className="space-y-1 max-h-44 overflow-y-auto">
                        {usersNotInstalled.map((u) => (
                          <li key={u.userId} className="flex items-start gap-2 text-sm">
                            <input
                              type="checkbox"
                              className="mt-1 h-4 w-4"
                              checked={selectedInstallUsers.has(u.userId)}
                              onChange={() => toggleSelection(setSelectedInstallUsers, u.userId)}
                              disabled={Boolean(busy)}
                            />
                            <span className="min-w-0">
                              <span className="block text-gray-900 dark:text-gray-100">{u.displayName}</span>
                              <span className="block text-xs text-gray-500 dark:text-gray-400">{u.email}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        variant="default"
                        size="sm"
                        className="mt-2"
                        disabled={Boolean(busy) || selectedInstallUsers.size === 0}
                        onClick={installUsers}
                      >
                        {busy === 'install-users' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Install selected
                      </Button>
                    </div>
                    <div className="rounded border border-gray-200 dark:border-gray-700 p-2">
                      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Installed ({usersInstalled.length})
                      </div>
                      <ul className="space-y-1 max-h-44 overflow-y-auto">
                        {usersInstalled.map((u) => (
                          <li key={u.userId} className="flex items-start gap-2 text-sm">
                            <input
                              type="checkbox"
                              className="mt-1 h-4 w-4"
                              checked={selectedUninstallUsers.has(u.userId)}
                              onChange={() => toggleSelection(setSelectedUninstallUsers, u.userId)}
                              disabled={Boolean(busy)}
                            />
                            <span className="min-w-0">
                              <span className="block text-gray-900 dark:text-gray-100">{u.displayName}</span>
                              <span className="block text-xs text-gray-500 dark:text-gray-400">{u.email}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="mt-2"
                        disabled={Boolean(busy) || selectedUninstallUsers.size === 0}
                        onClick={confirmUninstallUsers}
                      >
                        {busy === 'uninstall-users' ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : null}
                        Uninstall selected
                      </Button>
                    </div>
                  </div>
                </section>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
      <Dialog open={Boolean(deletionConfirmation)} onOpenChange={(next) => !next && setDeletionConfirmation(null)}>
        <DialogContent maxWidth="lg">
          <DialogHeader>
            <DialogTitle>{deletionConfirmationTitle}</DialogTitle>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 pr-8">
              {deletionConfirmationDescription}
            </p>
          </DialogHeader>
          <DialogBody>
            <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
              <div className="flex gap-2 text-sm text-red-800 dark:text-red-200">
                <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden />
                <span>
                  Review carefully. These changes affect external systems and are not just YAML edits.
                </span>
              </div>
            </div>
            <ul className="mt-4 max-h-64 overflow-y-auto space-y-2 text-sm">
              {deletionConfirmation?.items.map((item) => {
                const isUser = deletionConfirmation.kind === 'uninstall-users';
                const isIdentifier = deletionConfirmation.kind === 'uninstall-identifiers';
                const key = isUser
                  ? (item as RoleBotUserInstall).userId
                  : isIdentifier
                    ? String(item)
                  : (item as ProposedStep).id;
                const primary = isUser
                  ? (item as RoleBotUserInstall).displayName
                  : isIdentifier
                    ? String(item)
                  : (item as ProposedStep).resourceName;
                const secondary = isUser
                  ? (item as RoleBotUserInstall).email
                  : isIdentifier
                    ? 'Explicit user identifier'
                  : (item as ProposedStep).reason;
                return (
                  <li key={key} className="rounded border border-gray-200 dark:border-gray-700 p-2">
                    <div className="font-medium text-gray-900 dark:text-gray-100 break-all">
                      {primary}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 break-all">
                      {secondary}
                    </div>
                  </li>
                );
              })}
            </ul>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeletionConfirmation(null)}
              disabled={Boolean(busy)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeletion} disabled={Boolean(busy)}>
              Confirm deletion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
